import React, { FocusEventHandler, FormEventHandler, Fragment } from 'react';
import { connect } from 'dva';
import Radio from '@mos-cat/radio';
import { Heading } from '@mos-cat/typography';
import { Dispatch } from 'redux';
import { t, Trans } from '@lingui/macro';
import {
  getPayload,
  setErrorToSecondParentRelativeFields,
  trimOnBlur
} from '../../../helpers/handlers';
import { SecondParent } from './SecondParent';
import {
  PERSON_FIELD_NAME,
  SECOND_PARENT_FIELD_NAME
} from '../../../constants/fieldNames';
import TextField from '../../shared/TextField/TextField';
import { secondParentDeadSelector } from '../../../models/children';
import { Field } from '../../../declarations/widget-ums';
import { DEAD_PARENT } from '../../../constants/secondParentStatus';
import { getPersonPlaceholder } from '../../../helpers/getPlacelholder';
import { i18n } from '../../../helpers/i18n';
import {
  maxAcceptableDate,
  minAcceptableDate
} from '../../../helpers/dateHelpers';
import { CHILD } from '../../../models/child';
import { ERROR_DATE } from '../../../constants/errors';
import { getErrorLocation } from '../../../helpers/errorLocation';
import DateField from '../../shared/DateField/DateField';

interface Props {
  proofOfDeath: Field<string>;
  birthdate: Field<string>;
  deathdate: Field<string>;
  deathDocIssuer: Field<string>;
  showTitle: boolean;
  onChange: FormEventHandler;
  onBlur: FocusEventHandler;
  trimOnBlur: FocusEventHandler;
}

function SecondParentDead({
  onChange,
  onBlur,
  trimOnBlur,
  proofOfDeath,
  birthdate,
  deathdate,
  deathDocIssuer,
  showTitle
}: Props): JSX.Element {
  const { value, error, disabled } = proofOfDeath;
  const {
    birthdate: birthdateField,
    deathdate: deathdateField
  } = PERSON_FIELD_NAME;

  return (
    <div className="StepContentBlock">
      {showTitle && (
        <Heading level={3} className="PguFormGrid__row">
          <Trans>Сведения о втором родителе ребёнка</Trans>
        </Heading>
      )}
      <div className="PguFormGrid__row">
        <Radio
          label={i18n._(t`Имеется свидетельство о смерти второго родителя`)}
          name={SECOND_PARENT_FIELD_NAME.proofOfDeath}
          value={DEAD_PARENT.hasDeathCert}
          checked={value === DEAD_PARENT.hasDeathCert}
          error={error}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
      <div className="PguFormGrid__row">
        <Radio
          label={i18n._(
            t`Имеется вступившее в законную силу решение суда о признании второго родителя умершим`
          )}
          name={SECOND_PARENT_FIELD_NAME.proofOfDeath}
          value={DEAD_PARENT.courtDecision}
          checked={value === DEAD_PARENT.courtDecision}
          error={error}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
      <SecondParent />
      {value === DEAD_PARENT.hasDeathCert && (
        <Fragment>
          <div className="PguFormGrid__row">
            <div className="PguFormGrid_col">
              <DateField
                errorLocation={getErrorLocation(
                  birthdate,
                  ERROR_DATE.DEATHDATE_EARLY_BIRTHDATE
                )}
                {...birthdate}
                placeholder={getPersonPlaceholder(birthdateField)}
                name={birthdateField}
                onChange={onChange}
                minDate={minAcceptableDate}
                maxDate={maxAcceptableDate}
                onBlur={onBlur}
              />
            </div>
            <div className="PguFormGrid_col">
              <DateField
                errorLocation={getErrorLocation(
                  deathdate,
                  ERROR_DATE.DEATHDATE_EARLY_BIRTHDATE
                )}
                {...deathdate}
                placeholder={getPersonPlaceholder(deathdateField)}
                name={deathdateField}
                onChange={onChange}
                minDate={minAcceptableDate}
                maxDate={maxAcceptableDate}
                onBlur={onBlur}
              />
            </div>
          </div>
          <TextField
            className="PguFormGrid__row"
            placeholder={getPersonPlaceholder(
              SECOND_PARENT_FIELD_NAME.deathDocIssuer
            )}
            inputProps={{
              name: SECOND_PARENT_FIELD_NAME.deathDocIssuer,
              ...deathDocIssuer,
              onChange,
              onBlur: trimOnBlur,
              maxLength: 500
            }}
          />
        </Fragment>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChange(event) {
    const payload = getPayload(event);
    if (event.target.name === 'birthdate') {
      setErrorToSecondParentRelativeFields(dispatch, CHILD, ['deathdate']);
    }
    if (event.target.name === 'deathdate') {
      setErrorToSecondParentRelativeFields(dispatch, CHILD, ['birthdate']);
    }

    dispatch({
      type: 'child/secondParent/change',
      payload
    });
  },
  onBlur({ target: { name, value } }) {
    dispatch({
      type: `child/validate/field`,
      payload: { namespace: CHILD, name, value },
      additionalNamespace: 'secondParent'
    });
  },
  trimOnBlur: trimOnBlur('child/secondParent', dispatch)
});

export default connect(
  secondParentDeadSelector,
  mapDispatchToProps
)(SecondParentDead);
